import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { BimGroup, BimObject } from '../../models/Bim'
import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '../../store'
import { useDispatch, useSelector } from 'react-redux'
import { setSelectedBimObjectId } from '../../actions/bim'

const bimObjectsSelector = createSelector(
  [(state: RootState) => state.bim],
  (bim) => bim.objects,
)

const selectedBimObjectSelector = createSelector(
  [(state: RootState) => state.bim],
  (bim) => bim.objects.find((object) => object.id === bim.selectedBimObjectId),
)

interface BimLibraryCategoryListComponentProps {
  subCategories: BimGroup[] | undefined
}

export const BimLibraryCategoryListComponent = ({
  subCategories,
}: BimLibraryCategoryListComponentProps) => {
  const dispatch = useDispatch<any>()
  const bimObjects = useSelector(bimObjectsSelector)
  const selectedBimObject = useSelector(selectedBimObjectSelector)
  const boxRef = useRef<HTMLDivElement>(null)
  const [selectedSubCategory, setSelectedSubCategory] = useState('All')
  const [boxHeight, setBoxHeight] = useState(0)

  // Function to get filtered or all items based on selection
  const getItems = () => {
    if (selectedSubCategory === 'All') {
      const ids =
        subCategories?.flatMap((subCategory) => subCategory.objectIds || []) ||
        []
      return bimObjects.filter((object) => ids.includes(object.id))
    }
    const ids =
      subCategories?.find(
        (subCategory) => subCategory.name === selectedSubCategory,
      )?.objectIds || []
    return bimObjects.filter((object) => ids.includes(object.id))
  }

  const handleBimObjectClick = (object: BimObject) => {
    dispatch(setSelectedBimObjectId(object.id))
  }

  useEffect(() => {
    dispatch(setSelectedBimObjectId(null))
  }, [selectedSubCategory])

  useEffect(() => {
    const calculateHeight = () => {
      if (boxRef.current) {
        setBoxHeight(boxRef.current.clientHeight)
      }
    }

    calculateHeight()
    window.addEventListener('resize', calculateHeight)

    return () => {
      window.removeEventListener('resize', calculateHeight)
    }
  }, [])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        height: '100%',
        gap: '10px',
        alignItems: 'center',
        justifyContent: 'center',
        p: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          height: '100%',
          gap: '12px',
        }}
      >
        <Box
          sx={{
            width: '35%',
          }}
        >
          <List
            sx={{
              height: '100%',
              overflowY: 'auto',
              background: '#FBFBFB',
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '2px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '2px',
                '&:hover': {
                  background: '#666',
                },
              },
            }}
          >
            <ListItem disablePadding>
              <ListItemButton
                style={{
                  margin: '0 0.5rem',
                  padding: '0.5rem',
                  borderBottom: '0.1rem solid #E4E4E4',
                }}
                onClick={() => setSelectedSubCategory('All')}
                selected={selectedSubCategory === 'All'}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: '#E5F0FF',
                  },
                }}
              >
                <ListItemText
                  primary='All'
                  slotProps={{
                    primary: {
                      style: {
                        fontFamily: 'Inter',
                        fontWeight: 500,
                        fontStyle: 'Medium',
                        fontSize: '0.84rem',
                        lineHeight: '100%',
                        letterSpacing: '0%',
                        verticalAlign: 'middle',
                      },
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
            {subCategories?.map((subCategory: BimGroup, index: number) => (
              <ListItem key={index} disablePadding>
                <ListItemButton
                  style={{
                    margin: '0 0.5rem',
                    padding: '0.5rem',
                    borderBottom: '0.1rem solid #E4E4E4',
                  }}
                  onClick={() => setSelectedSubCategory(subCategory.name)}
                  selected={selectedSubCategory === subCategory.name}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: '#E5F0FF',
                    },
                  }}
                >
                  <ListItemText
                    primary={subCategory?.name
                      .toLocaleLowerCase()
                      .replaceAll('_', ' ')}
                    slotProps={{
                      primary: {
                        style: {
                          fontFamily: 'Inter',
                          fontWeight: 500,
                          fontStyle: 'Medium',
                          fontSize: '0.84rem',
                          lineHeight: '100%',
                          letterSpacing: '0%',
                          verticalAlign: 'middle',
                          textTransform: 'capitalize',
                        },
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
        <Box sx={{ width: '65%' }}>
          <Typography
            ref={boxRef}
            sx={{
              fontSize: '0.84rem',
              fontWeight: 600,
              padding: '4px',
              textAlign: 'left',
              width: '100%',
            }}
          >
            BIM Library Categories
          </Typography>
          <Box
            sx={{
              display: 'flex',
              background: '#FBFBFB',
              width: '100%',
              overflowY: 'auto',
              height: `calc(100% - ${boxHeight}px)`,
              padding: '0.5rem',
              boxSizing: 'border-box',
              borderRadius: '0.3rem',
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Grid container spacing={'0.5rem'} flexShrink={1}>
                {getItems().map((item: BimObject, index: number) => (
                  <Grid key={index} size={3}>
                    <Box
                      key={index}
                      component='button'
                      onClick={() => handleBimObjectClick(item)}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        alignItems: 'center',
                        background: '#fff',
                        borderRadius: 1,
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                        cursor: 'pointer',
                        border:
                          item.id === selectedBimObject?.id
                            ? '0.1rem solid #1705E5'
                            : 'none',
                        '&:hover': {
                          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                          transform: 'translateY(-2px)',
                          transition: 'all 0.2s ease-in-out',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          height: 80,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <img
                          alt={item.name}
                          src={
                            !item.thumbnailUrl
                              ? '/images/bim_default_image.png'
                              : `${item.thumbnailUrl}`
                          }
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            borderRadius: 4,
                          }}
                        />
                      </Box>
                      <Tooltip title={item.name} arrow placement='top'>
                        <Typography
                          sx={{
                            fontSize: 9,
                            textAlign: 'center',
                            fontWeight: 500,
                            color: '#04002E',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                          }}
                        >
                          {item.name}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
